import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, Briefcase, Globe, TrendingUp, ArrowRight } from "lucide-react";
import SEOHead from "../components/SEOHead";
import WhatsAppCTA from "../components/WhatsAppCTA";
import { useLocationBySlug } from "../hooks/useCMSData";
import { serviceCatalog } from "../data/services";

interface ServiceCatalogItem {
  slug: string;
  name: string;
}

const Spinner = () => (
  <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div style={{ width: 32, height: 32, borderRadius: "50%", borderTop: "2px solid #D4A843", animation: "spin 0.8s linear infinite" }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

export default function CityLandingPage() {
  const { countrySlug = "", citySlug = "" } = useParams();
  const { data: city, loading } = useLocationBySlug(citySlug);
  const [hoveredServiceIdx, setHoveredServiceIdx] = useState<number | null>(null);
  const [expandedFaqIdx, setExpandedFaqIdx] = useState<number | null>(null);

  if (loading) return <Spinner />;

  if (!city || city.countrySlug !== countrySlug) {
    return (
      <div style={{ minHeight: "60vh", padding: "120px 24px", color: "#F8F4EC", textAlign: "center" }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          <h1 style={{ fontSize: 42, marginBottom: 16 }}>City page not found.</h1>
          <p style={{ color: "#8B8378", fontSize: 16, marginBottom: 24 }}>
            The location you're looking for doesn't exist or may have been moved.
          </p>
          <Link
            to={`/${countrySlug}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "#D4A843",
              color: "#050505",
              padding: "12px 24px",
              borderRadius: 4,
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            Back to {countrySlug}
            <ChevronRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  // Sample stats for the city
  const cityStats = [
    { label: "Years of Experience", value: "12+" },
    { label: "Projects Completed", value: "500+" },
    { label: "Happy Clients", value: "200+" },
    { label: "Team Members", value: "50+" },
  ];

  // Sample FAQs for cities
  const cityFaqs = [
    {
      question: "Do you serve ${city.city} specifically?",
      answer: `Yes, we have extensive experience serving businesses in ${city.city}. We understand the local market dynamics, business culture, and regulatory environment, allowing us to deliver solutions tailored to your needs.`,
    },
    {
      question: "What's your process for local projects?",
      answer: `We start with a discovery phase to understand your local business landscape, then develop a customized strategy. We provide regular updates and maintain direct communication throughout the project.`,
    },
    {
      question: "Can you handle urgent timelines?",
      answer: `Yes, we can work within tight timelines when needed. Our agile process allows us to deliver results quickly without compromising quality. Contact our team to discuss your specific timeline.`,
    },
    {
      question: "Do you offer ongoing support?",
      answer: `Absolutely. We offer monthly retainers for support, maintenance, updates, and continuous improvement. Our team remains available for any issues or enhancements.`,
    },
  ];

  const filteredServices = (serviceCatalog as ServiceCatalogItem[]).slice(0, 12);

  return (
    <>
      <SEOHead
        title={`${city.city} Digital & Technology Services | Obsidian Web Labs`}
        description={`Serving businesses in ${city.city}, ${city.country}. Expert web development, AI solutions, custom software, and digital transformation.`}
        canonicalUrl={`https://obsidianweblabs.com/${countrySlug}/${citySlug}`}
      />

      {/* HERO SECTION - Enhanced */}
      <section
        className="obsidian-bg-texture owl-city-hero"
        style={{
          background: "#050505",
          color: "#F8F4EC",
          minHeight: "100vh",
          padding: "160px clamp(20px, 4vw, 48px) 100px",
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative background elements */}
        <div
          style={{
            position: "absolute",
            top: "10%",
            right: "-5%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(212,168,67,0.15) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "10%",
            left: "-10%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(212,168,67,0.1) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2, width: "100%" }}>
          {/* Breadcrumb */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32 }}>
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                color: "#908A83",
              }}
            >
              Serving
            </span>
            <span
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 700,
                fontSize: 11,
                color: "#D4A843",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
              }}
            >
              {city.city}, {city.country}
            </span>
          </div>

          {/* Main Heading */}
          <h1 style={{ marginBottom: 24 }}>
            <span
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 300,
                fontSize: "clamp(48px, 7vw, 88px)",
                color: "#F8F4EC",
                letterSpacing: "-0.02em",
                lineHeight: 0.95,
                display: "block",
              }}
            >
              Technology & Digital
            </span>
            <span
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "clamp(48px, 7vw, 88px)",
                color: "#D4A843",
                letterSpacing: "0.02em",
                lineHeight: 0.95,
                display: "block",
              }}
            >
              Solutions for {city.city}
            </span>
          </h1>

          {/* Subheading */}
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "clamp(16px, 2vw, 20px)",
              color: "#B8B2A8",
              lineHeight: 1.8,
              maxWidth: 680,
              marginBottom: 40,
              fontWeight: 300,
            }}
          >
            {city.marketContext} {city.businessCulture}
          </p>

          {/* Divider */}
          <div
            style={{
              width: 60,
              height: "1px",
              background:
                "linear-gradient(to right, #D4A843, transparent)",
              marginBottom: 40,
            }}
          />

          {/* CTA Buttons */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 32 }}>
            <WhatsAppCTA
              serviceName="technology solutions"
              locationName={city.city}
              variant="hero"
            />
            <Link
              to="/contact"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "14px 32px",
                borderRadius: 3,
                border: "1px solid #D4A843",
                color: "#D4A843",
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 13,
                letterSpacing: "0.2em",
                textDecoration: "none",
                textTransform: "uppercase",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#D4A843";
                e.currentTarget.style.color = "#050505";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "#D4A843";
              }}
            >
              Schedule Consultation
              <ArrowRight size={14} />
            </Link>
          </div>

          {/* Trust Indicators */}
          <div
            style={{
              display: "flex",
              gap: 32,
              flexWrap: "wrap",
              paddingTop: 32,
              borderTop: "1px solid rgba(212,168,67,0.15)",
            }}
          >
            {[
              { icon: "500+", label: "Projects Delivered" },
              { icon: "200+", label: "Happy Clients" },
              { icon: "15+", label: "Industries Served" },
              { icon: "12+", label: "Years Experience" },
            ].map((item, idx) => (
              <div key={idx} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div
                  style={{
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: 20,
                    color: "#D4A843",
                    fontWeight: 700,
                  }}
                >
                  {item.icon}
                </div>
                <div
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13,
                    color: "#908A83",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES SECTION - Enhanced */}
      <section
        style={{
          background: "#0E0E0E",
          borderTop: "1px solid rgba(212,168,67,0.12)",
          padding: "100px clamp(20px, 4vw, 48px)",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ marginBottom: 60 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 24,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#D4A843",
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: "0.3em",
                  color: "#D4A843",
                  textTransform: "uppercase",
                  display: "inline-block",
                }}
              >
                Industries We Serve
              </span>
            </div>

            <h2
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "clamp(36px, 5vw, 64px)",
                color: "#F8F4EC",
                lineHeight: 1,
              }}
            >
              Sectors driving {city.city}'s growth.
            </h2>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
            }}
          >
            {city.keyIndustries.map((industry, idx) => (
              <div
                key={industry}
                style={{
                  padding: "24px",
                  background: "#1A1208",
                  border: "1px solid rgba(212,168,67,0.25)",
                  borderRadius: 4,
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  animation: `fadeInUp 0.6s ease ${idx * 0.08}s both`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor =
                    "rgba(212,168,67,0.6)";
                  e.currentTarget.style.background = "#222017";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor =
                    "rgba(212,168,67,0.25)";
                  e.currentTarget.style.background = "#1A1208";
                }}
              >
                <div
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 15,
                    color: "#F8F4EC",
                    fontWeight: 500,
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {idx % 3 === 0 && <Briefcase size={16} color="#D4A843" />}
                  {idx % 3 === 1 && <Globe size={16} color="#D4A843" />}
                  {idx % 3 === 2 && <TrendingUp size={16} color="#D4A843" />}
                  {industry}
                </div>
                <p
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 12,
                    color: "#908A83",
                    lineHeight: 1.5,
                  }}
                >
                  Custom solutions tailored to this sector's unique needs.
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES SECTION - Enhanced */}
      <section
        style={{
          background: "#050505",
          borderTop: "1px solid rgba(212,168,67,0.12)",
          padding: "100px clamp(20px, 4vw, 48px)",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ marginBottom: 60 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 24,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#D4A843",
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: "0.3em",
                  color: "#D4A843",
                  textTransform: "uppercase",
                  display: "inline-block",
                }}
              >
                Full Service Suite
              </span>
            </div>

            <h2
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "clamp(36px, 5vw, 64px)",
                color: "#F8F4EC",
                lineHeight: 1,
                marginBottom: 16,
              }}
            >
              Everything you need in one place.
            </h2>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16,
                color: "#908A83",
                maxWidth: 600,
                lineHeight: 1.6,
              }}
            >
              From web development and AI to enterprise software and digital marketing — we're a one-stop shop for ${city.city} businesses.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 20,
            }}
          >
            {filteredServices.map((service: ServiceCatalogItem, idx) => (
              <Link
                key={service.slug}
                to={`/services/${service.slug}/${city.slug}`}
                style={{
                  padding: "32px",
                  background: hoveredServiceIdx === idx ? "#0E0E0E" : "#1A1208",
                  border:
                    hoveredServiceIdx === idx
                      ? "1px solid rgba(212,168,67,0.5)"
                      : "1px solid rgba(212,168,67,0.2)",
                  borderRadius: 4,
                  textDecoration: "none",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 200,
                }}
                onMouseEnter={() => setHoveredServiceIdx(idx)}
                onMouseLeave={() => setHoveredServiceIdx(null)}
              >
                <div
                  style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 700,
                    fontSize: 10,
                    letterSpacing: "0.3em",
                    color: "#D4A843",
                    textTransform: "uppercase",
                    marginBottom: 16,
                  }}
                >
                  Service {String(idx + 1).padStart(2, "0")}
                </div>

                <h3
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "clamp(18px, 2vw, 24px)",
                    color: "#F8F4EC",
                    fontWeight: 600,
                    marginBottom: 12,
                    flex: 1,
                  }}
                >
                  {service.name}
                </h3>

                <p
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13,
                    color: "#908A83",
                    marginBottom: 20,
                    lineHeight: 1.5,
                  }}
                >
                  Expert implementation tailored to businesses in {city.city}.
                </p>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 700,
                    fontSize: 11,
                    color: "#D4A843",
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                  }}
                >
                  Learn More
                  <ArrowRight
                    size={14}
                    style={{
                      transition: "transform 0.3s ease",
                      transform:
                        hoveredServiceIdx === idx
                          ? "translateX(4px)"
                          : "none",
                    }}
                  />
                </div>
              </Link>
            ))}
          </div>

          <div style={{ textAlign: "center", marginTop: 60 }}>
            <Link
              to="/services"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "14px 32px",
                background: "#D4A843",
                color: "#050505",
                borderRadius: 3,
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 13,
                letterSpacing: "0.2em",
                textDecoration: "none",
                textTransform: "uppercase",
                transition: "all 0.3s ease",
                fontWeight: 700,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#B8912E";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#D4A843";
              }}
            >
              View All Services
              <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* STATS SECTION */}
      <section
        style={{
          background: "#0E0E0E",
          borderTop: "1px solid rgba(212,168,67,0.12)",
          padding: "100px clamp(20px, 4vw, 48px)",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <h2
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "clamp(32px, 4vw, 56px)",
              color: "#F8F4EC",
              lineHeight: 1,
              marginBottom: 60,
              textAlign: "center",
            }}
          >
            Why businesses choose OWL.
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 32,
            }}
          >
            {cityStats.map((stat, idx) => (
              <div
                key={stat.label}
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  borderBottom: "1px solid rgba(212,168,67,0.2)",
                  animation: `fadeInUp 0.6s ease ${idx * 0.1}s both`,
                }}
              >
                <div
                  style={{
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: "clamp(36px, 5vw, 64px)",
                    color: "#D4A843",
                    lineHeight: 1,
                    marginBottom: 12,
                  }}
                >
                  {stat.value}
                </div>
                <div
                  style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontSize: 12,
                    letterSpacing: "0.2em",
                    color: "#908A83",
                    textTransform: "uppercase",
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section
        style={{
          background: "#050505",
          borderTop: "1px solid rgba(212,168,67,0.12)",
          padding: "100px clamp(20px, 4vw, 48px)",
        }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ marginBottom: 60, textAlign: "center" }}>
            <h2
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "clamp(32px, 4vw, 56px)",
                color: "#F8F4EC",
                lineHeight: 1,
              }}
            >
              Common Questions
            </h2>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {cityFaqs.map((faq, idx) => (
              <div
                key={idx}
                style={{
                  border: "1px solid rgba(212,168,67,0.2)",
                  borderRadius: 4,
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                }}
              >
                <button
                  onClick={() =>
                    setExpandedFaqIdx(
                      expandedFaqIdx === idx ? null : idx
                    )
                  }
                  style={{
                    width: "100%",
                    padding: "24px",
                    background:
                      expandedFaqIdx === idx ? "#0E0E0E" : "transparent",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 16,
                      color: "#F8F4EC",
                      fontWeight: 500,
                      textAlign: "left",
                    }}
                  >
                    {faq.question}
                  </span>
                  <span
                    style={{
                      color: "#D4A843",
                      transition: "transform 0.3s ease",
                      transform:
                        expandedFaqIdx === idx
                          ? "rotate(180deg)"
                          : "none",
                      flexShrink: 0,
                    }}
                  >
                    ▾
                  </span>
                </button>

                {expandedFaqIdx === idx && (
                  <div
                    style={{
                      padding: "0 24px 24px",
                      borderTop: "1px solid rgba(212,168,67,0.12)",
                      background: "#0A0A0A",
                    }}
                  >
                    <p
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 14,
                        color: "#8B8378",
                        lineHeight: 1.7,
                      }}
                    >
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section
        style={{
          background: "#0E0E0E",
          borderTop: "1px solid rgba(212,168,67,0.12)",
          padding: "100px clamp(20px, 4vw, 48px)",
        }}
      >
        <div
          style={{
            maxWidth: 900,
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "clamp(36px, 5vw, 64px)",
              color: "#F8F4EC",
              lineHeight: 1,
              marginBottom: 24,
            }}
          >
            Ready to transform
          </h2>
          <h2
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "clamp(36px, 5vw, 64px)",
              color: "#D4A843",
              lineHeight: 1,
              marginBottom: 32,
            }}
          >
            your {city.city} business?
          </h2>

          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              color: "#908A83",
              lineHeight: 1.7,
              maxWidth: 600,
              margin: "0 auto 40px",
            }}
          >
            Let's build something great together. Schedule a consultation with our team to discuss your specific needs and goals.
          </p>

          <div
            style={{
              display: "flex",
              gap: 16,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <WhatsAppCTA
              serviceName="consultation"
              locationName={city.city}
              variant="inline"
            />
            <Link
              to="/contact"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "14px 32px",
                background: "#D4A843",
                color: "#050505",
                borderRadius: 3,
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 13,
                letterSpacing: "0.2em",
                textDecoration: "none",
                textTransform: "uppercase",
                transition: "all 0.3s ease",
                fontWeight: 700,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#B8912E";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#D4A843";
              }}
            >
              Get in Touch
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* BACK NAVIGATION */}
      <section
        style={{
          background: "#050505",
          borderTop: "1px solid rgba(212,168,67,0.12)",
          padding: "60px clamp(20px, 4vw, 48px)",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Link
            to={`/${city.countrySlug}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              color: "#D4A843",
              textDecoration: "none",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14,
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.gap = "12px";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.gap = "8px";
            }}
          >
            ← Back to {city.country} overview
          </Link>
        </div>
      </section>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .owl-services-grid,
          .owl-industries-grid {
            grid-template-columns: 1fr !important;
          }

          .owl-city-hero {
            padding-top: clamp(100px, 20vw, 140px) !important;
            padding-bottom: 64px !important;
            align-items: flex-start !important;
          }
        }
      `}</style>
    </>
  );
}
